import type { CSSProperties, HTMLAttributes, MouseEvent, RefObject } from 'react';

import { MoveIcon, classNames } from '@movingimage-evp/mi-ui-component-library';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './draggable-area.module.css';

type DraggableAreaProps = {
  enabled: boolean;
  containerRef: RefObject<HTMLDivElement | null>;
  onStop: (style?: CSSProperties) => void;
} & HTMLAttributes<HTMLDivElement>;

const MIN_DESKTOP_WIDTH = 960;

export function DraggableArea({ enabled, containerRef, children, onStop, ...props }: DraggableAreaProps) {
  const { t } = useTranslation();
  const elementRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [childActionsDisabled, setChildActionsDisabled] = useState(false);
  const [initialMousePosition, setInitialMousePosition] = useState({ x: 0, y: 0 });
  const [initialElementPosition, setInitialElementPosition] = useState({ x: 0, y: 0 });
  const [style, setStyle] = useState<CSSProperties | undefined>(props.style);

  const element = elementRef.current;
  const container = containerRef.current;

  const startDragging = (event: MouseEvent<HTMLDivElement>) => {
    if (!enabled || !element || !container) return;

    event.preventDefault();

    const { clientX, clientY } = event;
    const { left: draggableAreaLeft, top: draggableAreaTop } = element.getBoundingClientRect();
    const { left: containerLeft, top: containerTop } = container.getBoundingClientRect();

    setInitialMousePosition({ x: clientX, y: clientY });
    setInitialElementPosition({ x: draggableAreaLeft - containerLeft, y: draggableAreaTop - containerTop });
    setIsDragging(true);
  };

  useEffect(() => {
    const calculateNewPosition = (initialX: number, initialY: number, deltaX: number, deltaY: number) => {
      if (!enabled || !element || !container) return;

      const { width: containerWidth, height: containerHeight } = container.getBoundingClientRect();
      const { width: elementWidth, height: elementHeight } = element.getBoundingClientRect();

      const maxX = containerWidth - elementWidth;
      const maxY = containerHeight - elementHeight;
      const newLeft = Math.min(Math.max(initialX + deltaX, 0), maxX);
      const newTop = Math.min(Math.max(initialY + deltaY, 0), maxY);

      return {
        left: `${newLeft}px`,
        top: `${newTop}px`,
      };
    };

    const drag = (event: globalThis.MouseEvent) => {
      if (!enabled || !isDragging || !element || !container) return;

      event.preventDefault();

      const { clientX, clientY } = event;
      const { x: initialMouseX, y: initialMouseY } = initialMousePosition;
      const { x: initialElementX, y: initialElementY } = initialElementPosition;

      const deltaX = clientX - initialMouseX;
      const deltaY = clientY - initialMouseY;

      setChildActionsDisabled(true);
      setStyle(calculateNewPosition(initialElementX, initialElementY, deltaX, deltaY));
    };

    const keepDraggableAreaInsideContainer = () => {
      if (!enabled || window.innerWidth < MIN_DESKTOP_WIDTH || !element || !container) return;

      const {
        top: containerTop,
        left: containerLeft,
        width: containerWidth,
        height: containerHeight,
      } = container.getBoundingClientRect();

      const {
        top: elementTop,
        left: elementLeft,
        width: elementWidth,
        height: elementeHeight,
      } = element.getBoundingClientRect();

      const elementIsOutsideContainer =
        elementTop < containerTop ||
        elementLeft < containerLeft ||
        elementLeft + elementWidth > containerLeft + containerWidth ||
        elementTop + elementeHeight > containerTop + containerHeight;

      if (!elementIsOutsideContainer) return;

      const deltaX = elementLeft - containerLeft;
      const deltaY = elementTop - containerTop;

      setStyle(calculateNewPosition(0, 0, deltaX, deltaY));
    };

    const stopDragging = () => {
      if (!enabled || !isDragging) return;

      setIsDragging(false);
      setChildActionsDisabled(false);
      onStop(style);
    };

    if (!enabled) return;

    window.addEventListener('mousemove', drag);
    window.addEventListener('mouseup', stopDragging);
    window.addEventListener('resize', keepDraggableAreaInsideContainer);

    return () => {
      window.removeEventListener('mousemove', drag);
      window.removeEventListener('mouseup', stopDragging);
      window.removeEventListener('resize', keepDraggableAreaInsideContainer);
    };
  }, [container, enabled, element, initialElementPosition, initialMousePosition, isDragging, onStop, style]);

  useEffect(() => {
    if (enabled) setStyle(props.style);
  }, [enabled, props.style]);

  return (
    <div
      ref={elementRef}
      onMouseDown={startDragging}
      {...props}
      style={style}
      className={classNames(
        styles.wrapper,
        isDragging && styles.dragging,
        childActionsDisabled && styles.childActionsDisabled,
        enabled && styles.enabled,
        enabled && props.className
      )}
    >
      {enabled && (
        <span className={styles.info}>
          <MoveIcon />
          {t('viewer.views.stream.dragInfo')}
        </span>
      )}

      {children}
    </div>
  );
}
